import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { OAuthScopeList } from '../components/OAuthScopeList';
import api, { OAuthAuthorizeInfo } from '@/lib/api';

type PageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; info: OAuthAuthorizeInfo }
  | { status: 'submitting' };

export function OAuthConsentPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: 'loading' });

  const clientId = searchParams.get('client_id') || '';
  const redirectUri = searchParams.get('redirect_uri') || '';
  const responseType = searchParams.get('response_type') || '';
  const scope = searchParams.get('scope') || '';
  const stateParam = searchParams.get('state') || '';
  const codeChallenge = searchParams.get('code_challenge') || '';
  const codeChallengeMethod = searchParams.get('code_challenge_method') || '';

  useEffect(() => {
    if (!clientId || !redirectUri || !scope || !codeChallenge) {
      setState({ status: 'error', message: 'Missing required parameters' });
      return;
    }

    api
      .getOAuthAuthorizeInfo({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType,
        scope,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod,
      })
      .then((info) => setState({ status: 'ready', info }))
      .catch((err) =>
        setState({ status: 'error', message: err.message || 'Failed to load authorization info' }),
      );
  }, [clientId, redirectUri, responseType, scope, codeChallenge, codeChallengeMethod]);

  async function handleConsent(consent: 'granted' | 'denied') {
    setState({ status: 'submitting' });
    try {
      const result = await api.submitOAuthConsent({
        clientId,
        redirectUri,
        responseType,
        scope,
        state: stateParam,
        codeChallenge,
        codeChallengeMethod,
        consent,
      });
      // Redirect to the app's callback URL
      window.location.href = result.redirectUrl;
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to process consent',
      });
    }
  }

  if (state.status === 'loading' || state.status === 'submitting') {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {state.status === 'submitting' ? 'Processing...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <h2 className="text-lg font-semibold text-destructive">Authorization Error</h2>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{state.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { info } = state;

  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {info.app.logoUrl && (
            <img
              src={info.app.logoUrl}
              alt={info.app.name}
              className="h-12 w-12 rounded-lg mx-auto mb-2"
            />
          )}
          <h2 className="text-lg font-semibold">Authorize {info.app.name}</h2>
          {info.app.description && (
            <p className="text-sm text-muted-foreground mt-1">{info.app.description}</p>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-2">This app is requesting permission to:</h3>
            <OAuthScopeList scopes={info.scopes} />
          </div>

          <p className="text-xs text-muted-foreground">
            You will be redirected to <span className="font-mono">{info.redirectDomain}</span>
          </p>
        </CardContent>

        <CardFooter className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => handleConsent('denied')}>
            Deny
          </Button>
          <Button className="flex-1" onClick={() => handleConsent('granted')}>
            Authorize
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
